import { useQuery } from "react-query"
import { IConsultantDeclarationMensuelleFactureModel } from "@common-models/*"
import { AxiosError } from "axios"
import { getDeclarationActiveFactures } from "../../core/_requests"
import { useConsultantDeclarationDetailsContext } from "../_ConsultantDeclarationDetails"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyAlert from "@common-utils/MyAlert"
import Footer from "./Footer"
import React, { useState } from "react"
import MyGoogleDriveFileViewerInModal from "../../../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"

const ConsultantDeclarationDetails_Factures = () => {
     const context = useConsultantDeclarationDetailsContext()

     const [documentInModal, setActiveDriveDocumentInModal] = useState<{
          driveID: string
          driveMime: string
          modalTitle: string
          isLoading: boolean
     }>()

     const facturesQuery = useQuery<IConsultantDeclarationMensuelleFactureModel[], AxiosError>("GET_FACTURES", () =>
          getDeclarationActiveFactures(context.declaration.id!).then(r => r.data)
     )

     // Loading State
     if (facturesQuery.isFetching) {
          return (
               <div className={"text-center p-5"}>
                    <MySimpleSpinner size={"xl"} />
               </div>
          )
     }

     // Error State
     if (!facturesQuery.isFetching && facturesQuery.isError) {
          return (
               <MyAlert type={"danger"} classNames={"mb-4"}>
                    <>
                         <span className={"me-2"}>{facturesQuery.error?.response?.data?.detail}</span>
                         <button className={"btn btn-sm btn-danger"} onClick={() => facturesQuery.refetch()}>
                              Recharger
                         </button>
                    </>
               </MyAlert>
          )
     }

     // Empty State
     if (!facturesQuery.isFetching && !facturesQuery.isError && facturesQuery.data && facturesQuery.data.length === 0) {
          return (
               <div className={"text-center p-4"}>
                    <p style={{ fontWeight: 500 }} className={"mb-4"}>
                         Aucune facture n'a été ajoutée ...
                    </p>
                    <Footer />
               </div>
          )
     }

     return (
          <div className="animate__animated animate__fadeIn">
               <div className="mb-10">
                    {facturesQuery.data!.map((item, index) => {
                         // Calcul sécurisé du total (si HT ou TVA absents, ils sont traités comme 0)
                         const total = ((item.montantHt || 0) + (item.montantTva || 0)).toFixed(2)

                         // Vérification de l'existence des montants pour l'affichage des badges [cite: 66, 67]
                         const hasHt = item.montantHt !== null && item.montantHt !== undefined
                         const hasTva = item.montantTva !== null && item.montantTva !== undefined

                         return (
                              <div
                                   key={item.id || index}
                                   className="d-flex flex-column flex-md-row align-items-md-center justify-content-between bg-body border border-secondary border-opacity-25 rounded px-5 py-4 mb-4 hover-elevate-up shadow-xs transition-all"
                              >
                                   {/* Left Side: Icon and Info */}
                                   <div className="d-flex align-items-center mb-3 mb-md-0 flex-grow-1">
                                        <div className="symbol symbol-40px bg-light-primary border border-primary border-opacity-10 me-5 d-flex align-items-center justify-content-center">
                                             <i className="fas fa-file-invoice text-primary fs-5"></i>
                                        </div>
                                        <div className="d-flex flex-column me-8">
                                             <span className="text-gray-900 fw-bold fs-5">Facture n°{index + 1}</span>
                                             {item.createdAt && <span className="text-muted fs-7">Déposée le {item.createdAt.format("DD/MM/YYYY")}</span>}
                                        </div>

                                        {/* Amounts breakdown (hidden on mobile) - Conditionné par l'existence des montants [cite: 67, 68] */}
                                        {(hasHt || hasTva) && (
                                             <div className="text-gray-600 fs-7 fw-semibold d-none d-lg-block">
                                                  {hasHt && <span className="badge badge-secondary me-2">HT: {item.montantHt?.toFixed(2)}€</span>}
                                                  {hasTva && <span className="badge badge-secondary">TVA: {item.montantTva?.toFixed(2)}€</span>}
                                             </div>
                                        )}
                                   </div>

                                   {/* Right Side: Total and View Button */}
                                   <div className="d-flex align-items-center justify-content-between justify-content-md-end w-100 w-md-auto gap-4">
                                        {total && Number(total) > 0 && <span className="badge badge-light-dark fw-bolder fs-6 px-4 py-2 me-md-2">{total} €</span>}

                                        <button
                                             className="btn btn-sm btn-light-primary fw-bold"
                                             onClick={() =>
                                                  setActiveDriveDocumentInModal({
                                                       modalTitle: `Facture n°${index + 1}`,
                                                       driveID: item.googleDriveId!,
                                                       driveMime: item.googleDriveMimeType!,
                                                       isLoading: true,
                                                  })
                                             }
                                        >
                                             <i className="fas fa-eye me-2"></i>
                                             Voir
                                        </button>
                                   </div>
                              </div>
                         )
                    })}
               </div>

               <div className="separator separator-dashed my-5"></div>
               <Footer />

               {/* Modal Document Viewer */}
               {documentInModal && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={documentInModal.driveID as string}
                         googleDriveMimetype={documentInModal.driveMime as string}
                         modalTitle={<span>{documentInModal.modalTitle}</span>}
                         handleCloseModal={() => setActiveDriveDocumentInModal(undefined)}
                         modalSize={"xl"}
                    />
               )}
          </div>
     )
}

export default ConsultantDeclarationDetails_Factures
